import { Controller } from "@hotwired/stimulus"

// Image picker for the website-builder block editor.
//
// Renders inline as: [thumbnail] [Choose image] [Clear]
//
// Clicking "Choose image" opens a grid of every image in the account's
// public_site_images library (fetched once per open). Selecting one
// writes the attachment id into a hidden <input data-page-editor-target="setting">
// and dispatches a change event so the page editor's auto-save picks it up.
//
// "Upload" inside the picker POSTs a multipart form to the library, then
// re-renders the grid with the new image on top.
//
// Values:
//   indexUrl  — GET .json endpoint returning { images: [{ id, url, thumb_url, filename }] }
//   uploadUrl — POST multipart to add an image (same endpoint, accept JSON)
export default class extends Controller {
  static targets = ["hiddenInput", "thumb", "thumbWrap", "emptyState", "clearButton", "grid", "uploader"]
  static values  = { indexUrl: String, uploadUrl: String, logoUrl: String }

  open(event) {
    event?.preventDefault()
    this.ensureModal()
    this.modal.hidden = false
    this.loadImages()
  }

  close = (event) => {
    event?.preventDefault()
    if (this.modal) this.modal.hidden = true
  }

  // Clears the selection. Block will fall back to its default image.
  clear(event) {
    event?.preventDefault()
    this.setSelection(null)
  }

  pick = (event) => {
    const btn = event.currentTarget
    const id = btn.dataset.imageId
    const url = btn.dataset.imageUrl
    this.setSelection({ id, url })
    this.close()
  }

  uploadChanged = async (event) => {
    const input = event.currentTarget
    const files = Array.from(input.files || [])
    if (!files.length) return

    const fd = new FormData()
    files.forEach(f => fd.append("images[]", f))

    const csrf = document.querySelector("meta[name='csrf-token']")?.content
    const res = await fetch(this.uploadUrlValue, {
      method: "POST",
      headers: { "Accept": "application/json", "X-CSRF-Token": csrf || "" },
      body: fd,
      credentials: "same-origin"
    })

    input.value = ""
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      this.statusEl.textContent = data.error || "Upload failed."
      return
    }
    const data = await res.json()
    this.renderGrid(data.images || [])

    // Auto-select the newest image (first in list since controller orders desc)
    const newest = (data.images || [])[0]
    if (newest) this.setSelection({ id: newest.id, url: newest.url })
  }

  async loadImages() {
    this.statusEl.textContent = "Loading…"
    try {
      const res = await fetch(this.indexUrlValue, { headers: { "Accept": "application/json" }, credentials: "same-origin" })
      const data = await res.json()
      this.renderGrid(data.images || [])
    } catch (_) {
      this.statusEl.textContent = "Couldn't load images."
    }
  }

  renderGrid(images) {
    this.statusEl.textContent = images.length ? "" : "No images yet — upload your first one."
    this.gridEl.innerHTML = ""
    const selectedId = this.hiddenInputTarget.value

    // The brand logo rides along as a special tile (id "logo") — no
    // need to re-upload it to the photo library.
    if (this.hasLogoUrlValue && this.logoUrlValue) {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.imageId = "logo"
      btn.dataset.imageUrl = this.logoUrlValue
      btn.className = "group relative aspect-[4/3] overflow-hidden rounded border bg-gray-50 dark:bg-gray-800 " +
        (selectedId === "logo" ? "border-blue-500 ring-2 ring-blue-300" : "border-gray-200 hover:border-gray-400")
      btn.innerHTML = `
        <img src="${this.logoUrlValue}" alt="Your logo" class="w-full h-full object-contain p-3" />
        <span class="absolute inset-x-0 bottom-0 px-2 py-1 text-[10px] truncate text-white bg-black/55">Your logo</span>
      `
      btn.addEventListener("click", this.pick)
      this.gridEl.appendChild(btn)
    }

    images.forEach(img => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.imageId = img.id
      btn.dataset.imageUrl = img.url
      btn.className = "group relative aspect-[4/3] overflow-hidden rounded border " +
        (String(img.id) === String(selectedId) ? "border-blue-500 ring-2 ring-blue-300" : "border-gray-200 hover:border-gray-400")
      btn.innerHTML = `
        <img src="${img.thumb_url}" alt="${img.filename}" class="w-full h-full object-cover" />
        <span class="absolute inset-x-0 bottom-0 px-2 py-1 text-[10px] truncate text-white bg-black/55">${img.filename}</span>
      `
      btn.addEventListener("click", this.pick)
      this.gridEl.appendChild(btn)
    })
  }

  setSelection(picked) {
    // picked: { id, url } or null
    this.hiddenInputTarget.value = picked ? picked.id : ""
    this.hiddenInputTarget.dispatchEvent(new Event("input",  { bubbles: true }))
    this.hiddenInputTarget.dispatchEvent(new Event("change", { bubbles: true }))

    if (picked && picked.url) {
      this.thumbTarget.src = picked.url
      this.thumbWrapTarget.hidden = false
      this.emptyStateTarget.hidden = true
      this.clearButtonTarget.hidden = false
    } else {
      this.thumbWrapTarget.hidden = true
      this.emptyStateTarget.hidden = false
      this.clearButtonTarget.hidden = true
    }
  }

  // Lazily-built portal for the picker grid. Kept off the document until
  // first open so a page with 20 image fields doesn't render 20 modals.
  ensureModal() {
    if (this.modal) return
    const modal = document.createElement("div")
    modal.hidden = true
    modal.className = "fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
    modal.innerHTML = `
      <div class="bg-white dark:bg-gray-900 rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div class="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
          <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Choose an image</h3>
          <button type="button" data-close class="text-gray-400 hover:text-gray-600 text-xl leading-none">&times;</button>
        </div>
        <div class="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
          <label class="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium bg-gray-900 text-white rounded cursor-pointer hover:bg-black">
            <span>Upload new</span>
            <input data-uploader type="file" multiple accept="image/png,image/jpeg,image/webp,image/gif" class="hidden">
          </label>
          <p data-status class="text-xs text-gray-500 dark:text-gray-400"></p>
        </div>
        <div data-grid class="flex-1 overflow-y-auto p-4 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3"></div>
      </div>
    `
    document.body.appendChild(modal)

    this.modal     = modal
    this.gridEl    = modal.querySelector("[data-grid]")
    this.statusEl  = modal.querySelector("[data-status]")
    const uploader = modal.querySelector("[data-uploader]")
    uploader.addEventListener("change", this.uploadChanged)
    modal.querySelector("[data-close]").addEventListener("click", this.close)
    modal.addEventListener("click", (e) => { if (e.target === modal) this.close(e) })
  }

  disconnect() {
    if (this.modal) {
      this.modal.remove()
      this.modal = null
    }
  }
}
